import type { EstimateAssumption, EstimateExclusion, EstimatorInput, ProjectType } from "../../types/index.js";
import { getScopeMultiplier } from "../scope-multipliers.js";
import { applyItem, findItem, scaleAppliedItem, type AppliedLineItem, type ProjectCalculationOutput, type ProjectCalculatorFn } from "./shared.js";

/**
 * Phase 13A: Interior Room Estimators (living room, family room, dining
 * room, bedroom, primary bedroom, home office, laundry room, mudroom,
 * entryway/foyer, hallway).
 *
 * The brief is explicit that these ten project types must NOT become ten
 * separate hand-written calculation systems, but also must NOT collapse
 * into one generic room form with only the title changed - each has to
 * keep scope that materially affects its own cost (flooring, painting,
 * trim, lighting, built-ins/storage, windows/doors, plumbing where
 * applicable, and so on).
 *
 * This module is the resolution of that tension, using the same pattern
 * bathroom and basement already established: one shared calculation
 * *mechanism* (this factory), driven by a project-type-specific
 * *configuration* (see rules/living-room.ts, rules/mudroom.ts, etc). Each
 * of the ten project types gets its own small config file naming its own
 * pricing line items, its own question ids, and its own subset of
 * features (a hallway has no built-ins or windows/doors question at all;
 * a laundry room gets a dedicated plumbing scope tier no other type in
 * this batch has) - so the *scope* is genuinely project-specific even
 * though the *code path* that prices it is shared.
 *
 * Every "tiered feature" (flooring, painting, trim, lighting, built-ins,
 * windows/doors, and any project-specific tiered addition like laundry's
 * plumbing scope or home office's electrical/data scope) follows the same
 * rule: if the corresponding question wasn't answered, or was answered
 * with a "none"/"not changing"/"keep existing" tier, that category is
 * excluded from the estimate rather than defaulted to a guessed tier -
 * unlike kitchen/basement, which default a couple of specific categories.
 * This keeps the shared mechanism simple and honest: nothing is priced
 * that the user didn't actually select.
 */

function rawString(input: EstimatorInput, key: string): string | undefined {
  const value = input.rawAnswers[key];
  return typeof value === "string" ? value : undefined;
}

/** One tiered (single_select) scope category, e.g. flooring or lighting. */
export interface InteriorRoomTierFeature {
  /** The rawAnswers key this feature's question is stored under. */
  answerKey: string;
  /** Tier answer id -> pricing line item id, or null for a "none"/"not changing" tier that excludes this category. */
  itemIdByTier: Record<string, string | null>;
  /** True if this line item is priced per sqft of the room (flooring, painting); false/omitted for a flat item/allowance (lighting, built-ins, windows/doors). */
  quantityFromSqft?: boolean;
  /** Shown as an exclusion when the category resolves to no item (unanswered, or a "none" tier was chosen). */
  excludedMessage: string;
}

/** One boolean (yes/no) optional feature, e.g. a fireplace or heated floor. */
export interface InteriorRoomBooleanFeature {
  answerKey: string;
  itemId: string;
  quantityFromSqft?: boolean;
  excludedMessage: string;
}

export interface InteriorRoomCalculatorConfig {
  projectType: ProjectType;
  /** Base/anchor renovation line item, priced per sqft and scope-adjusted - the same pattern as bathroom_base_renovation / basement_finishing_base. */
  anchorItemId: string;
  tierFeatures: InteriorRoomTierFeature[];
  booleanFeatures?: InteriorRoomBooleanFeature[];
  /** Generic electrical allowance, gated on the common `electrical_changes === "yes"` flag. Omit for a project type whose electrical scope is instead one of its own tierFeatures (e.g. home office). */
  electricalItemId?: string;
  /** Generic plumbing allowance, gated on the common `plumbing_changes === "yes"` flag. Most of these ten project types have no dedicated plumbing pricing - only set this when a project type needs a plain plumbing-changes allowance distinct from any of its own tierFeatures/booleanFeatures. */
  plumbingItemId?: string;
  demolitionItemId?: string;
  accessibilityItemId?: string;
  permitDesignItemId?: string;
}

/**
 * Builds a `ProjectCalculatorFn` for one interior room project type from a
 * config object. This is the single piece of code that actually prices an
 * interior room estimate - every project type in this batch calls this
 * with its own configuration rather than re-implementing the same
 * anchor + scope + tiered-features + booleans + mechanical/demolition/
 * accessibility/permit pattern ten times over.
 */
export function createInteriorRoomCalculator(config: InteriorRoomCalculatorConfig): ProjectCalculatorFn {
  return (input, catalog, regionalMultiplier) => {
    const applied: AppliedLineItem[] = [];
    const assumptions: EstimateAssumption[] = [];
    const exclusions: EstimateExclusion[] = [];
    let anchorMissing = false;

    const finishLevel = input.finishLevel;
    const sqft = input.roomSize?.value;

    // --- Base renovation (anchor) + scope adjustment -------------------------
    let baseTypicalForPercentage = 0;
    if (sqft && sqft > 0) {
      const base = applyItem(catalog, config.anchorItemId, { quantity: sqft, finishLevel, regionalMultiplier });
      if (base) {
        const scopeMultiplier = getScopeMultiplier(input.scope);
        const scoped = scaleAppliedItem(base, scopeMultiplier);
        applied.push(scoped);
        baseTypicalForPercentage = scoped.result.typical?.amount ?? 0;
        assumptions.push({
          code: "scope_multiplier_applied",
          message: `Base renovation cost adjusted by a ${scopeMultiplier}x scope multiplier for "${input.scope}".`
        });
      }
    } else {
      anchorMissing = true;
    }

    // --- Tiered scope features (flooring, painting, trim, lighting, etc) ----
    for (const feature of config.tierFeatures) {
      const tier = rawString(input, feature.answerKey);
      const itemId = tier ? (feature.itemIdByTier[tier] ?? null) : null;
      if (itemId) {
        const params =
          feature.quantityFromSqft && sqft && sqft > 0 ? { quantity: sqft, finishLevel, regionalMultiplier } : { finishLevel, regionalMultiplier };
        const appliedFeature = applyItem(catalog, itemId, params);
        if (appliedFeature) applied.push(appliedFeature);
      } else {
        exclusions.push({ code: `excludes_${feature.answerKey}`, message: feature.excludedMessage });
      }
    }

    // --- Optional boolean features (fireplace, wet bar, heated floor, etc) --
    for (const feature of config.booleanFeatures ?? []) {
      if (input.rawAnswers[feature.answerKey] === true) {
        const params =
          feature.quantityFromSqft && sqft && sqft > 0 ? { quantity: sqft, finishLevel, regionalMultiplier } : { finishLevel, regionalMultiplier };
        const appliedFeature = applyItem(catalog, feature.itemId, params);
        if (appliedFeature) applied.push(appliedFeature);
      } else {
        exclusions.push({ code: `excludes_${feature.answerKey}`, message: feature.excludedMessage });
      }
    }

    // --- Mechanical: generic electrical/plumbing allowances ------------------
    if (config.electricalItemId && input.workType.electricalChanges === "yes") {
      const electrical = applyItem(catalog, config.electricalItemId, { finishLevel, regionalMultiplier });
      if (electrical) applied.push(electrical);
    }
    if (config.plumbingItemId && input.workType.plumbingChanges === "yes") {
      const plumbing = applyItem(catalog, config.plumbingItemId, { finishLevel, regionalMultiplier });
      if (plumbing) applied.push(plumbing);
    }

    // --- Demolition ------------------------------------------------------------
    if (config.demolitionItemId && input.workType.demolitionRequired === "yes" && findItem(catalog, config.demolitionItemId)) {
      const demolition = applyItem(catalog, config.demolitionItemId, { regionalMultiplier, baseAmountForPercentage: baseTypicalForPercentage });
      if (demolition) applied.push(demolition);
    }

    // --- Accessibility --------------------------------------------------------
    if (config.accessibilityItemId && input.accessibility.required === "yes") {
      const accessibility = applyItem(catalog, config.accessibilityItemId, { finishLevel, regionalMultiplier });
      if (accessibility) applied.push(accessibility);
    }

    // --- Permit / design allowance ---------------------------------------------
    if (config.permitDesignItemId && (input.designAndPermits.needsDesignServices === "yes" || input.designAndPermits.permitLikelyRequired !== "no")) {
      const permitDesign = applyItem(catalog, config.permitDesignItemId, { finishLevel, regionalMultiplier });
      if (permitDesign) applied.push(permitDesign);
    }

    const output: ProjectCalculationOutput = { applied, assumptions, exclusions, anchorMissing };
    return output;
  };
}
